import { useTranslations } from '@vocab/react';
import { Stack, Box, Text, useToast } from 'braid-design-system';
import { useEffect, useState, type ChangeEvent } from 'react';

import OptionsButton from 'src/components/atoms/Buttons/OptionsButton/OptionsButton';
import CommentCTA from 'src/components/atoms/CommentCTA/CommentCTA';
import CommentTextArea from 'src/components/atoms/CommentTextArea/CommentTextArea';
import ReactionBar from 'src/components/atoms/ReactionBar/ReactionBar';
import ThreadContent from 'src/components/atoms/ThreadContent/ThreadContent';
import ThreadPersonalDetails from 'src/components/atoms/ThreadPersonalDetails/ThreadPersonalDetails';
import {
  ContentValidationError,
  validateContent,
} from 'src/core/domain/errors';
import errorTranslations from 'src/core/domain/errors/.vocab';
import { ThreadCommentError } from 'src/core/domain/errors/threadCommentError';
import { handleAuthCheck } from 'src/core/utils/authUtil';
import { getNewCreationDate } from 'src/core/utils/seekMaxUtil';
import type { Thread } from 'src/mapper/threadMapper';
import { useAnalytics } from 'src/provider/analytics';
import { useAuthStatus } from 'src/provider/auth';
import { useConfig } from 'src/provider/config';
import { useLoginModal } from 'src/provider/loginModal';
import { useProfile } from 'src/provider/profile';
import { useThreadDetail } from 'src/provider/threadDetail';
import {
  updatePoll,
  updateThreadLikes,
  createThreadComment,
  type createThreadCommentParams,
  type updateThreadLikesParams,
} from 'src/services/threadInteractionService';

import translations from './.vocab';

import * as styles from './ThreadDetailsCard.css';

interface ThreadDetailsCardProps {
  threadDetails: Thread;
}

export default ({ threadDetails }: ThreadDetailsCardProps) => {
  const showToast = useToast();
  const { t } = useTranslations(translations);
  const { t: errorTranslation } = useTranslations(errorTranslations);
  const { trackAnalyticsInteraction } = useAnalytics();
  const { authStatus } = useAuthStatus();
  const { zone } = useConfig();
  const { openLoginDialog } = useLoginModal();
  const {
    threadDetailState: {
      thread,
      showCommentTextArea,
      commentText,
      discardState,
      error,
    },
    threadDetailDispatch,
  } = useThreadDetail();
  const {
    state: { author },
  } = useProfile();

  // Voting state
  const [isVoted, setIsVoted] = useState(
    thread?.poll?.options.some((option) => option?.voted) || false,
  );

  useEffect(() => {
    setIsVoted(thread?.poll?.options.some((option) => option?.voted) || false);
  }, [thread]);

  const handleLikeClick = async () => {
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;
    try {
      const params: updateThreadLikesParams = {
        id: thread.id,
        liked: !thread.liked,
      };
      const { countLabel, isLiked } = await updateThreadLikes(params);
      threadDetailDispatch({
        type: 'UPDATE_THREAD_LIKE',
        payload: { likeCountLabel: countLabel, liked: isLiked },
      });
      if (thread) {
        trackAnalyticsInteraction('LikeThreadClick', {
          threadId: thread.id,
          linkAction: !thread.liked ? 'like' : 'unlike',
          linkPosition: 'thread',
        });
      }
    } catch {
      // ignore
    }
  };

  const handleCommentClick = () => {
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;
    threadDetailDispatch({
      type: 'SET_COMMENT_TEXT_AREA_FOCUS',
      payload: { isFocus: true },
    });
    trackAnalyticsInteraction('CommentClick', {
      threadId: threadDetails.id,
    });
  };

  const handleCommentSubmit = async () => {
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;
    try {
      validateContent(commentText);
      const commentParams: createThreadCommentParams = {
        threadId: thread.id,
        content: commentText,
      };
      const { error: createThreadError, id } = await createThreadComment(
        commentParams,
      );
      if (createThreadError === null && id !== '') {
        const generateNewComment = {
          id: id || '',
          content: commentText,
          author,
          creationDate: getNewCreationDate().shortLabel,
          exactSocialData: {
            likeCountLabel: '0',
            replyCount: 0,
          },
          liked: false,
        };
        trackAnalyticsInteraction('CommentSubmit', {
          threadId: threadDetails.id,
          commentId: id,
        });
        showToast({
          message: t('commentPosted'),
          tone: 'positive',
        });
        threadDetailDispatch({
          type: 'CREATE_COMMENT',
          payload: {
            comment: generateNewComment,
          },
        });
        threadDetailDispatch({
          type: 'RESET_COMMENT',
        });
      } else if (createThreadError) {
        const errorToDisplay =
          createThreadError instanceof ContentValidationError ||
          createThreadError instanceof ThreadCommentError
            ? createThreadError
            : new ThreadCommentError();
        threadDetailDispatch({
          type: 'SET_ERROR',
          payload: { error: errorTranslation(errorToDisplay.key) },
        });
      }
    } catch (err) {
      const errorToDisplay =
        err instanceof ContentValidationError ||
        err instanceof ThreadCommentError
          ? err
          : new ThreadCommentError();
      threadDetailDispatch({
        type: 'SET_ERROR',
        payload: { error: errorTranslation(errorToDisplay.key) },
      });
    }
  };

  const handleDiscard = () => {
    threadDetailDispatch({
      type: 'RESET_COMMENT',
    });
  };

  const handleDiscardCancel = () => {
    threadDetailDispatch({
      type: 'SET_DISCARD_STATE',
      payload: { discardState: false },
    });
  };

  const handlePollClick = async (optionId: string) => {
    if (
      !handleAuthCheck({
        authStatus,
        openLoginDialog,
        trackAnalyticsInteraction,
      })
    )
      return;
    try {
      const params = {
        optionId,
        pollId: threadDetails.poll!.id,
        zone,
        voted: true,
      };
      const { voted } = await updatePoll(params);
      setIsVoted(voted);
      const selectedOptionTitle =
        threadDetails.poll?.options.find(({ id }) => id === optionId)?.title ||
        '';
      trackAnalyticsInteraction('ThreadPollClick', {
        threadId: threadDetails.id,
        linkAction: selectedOptionTitle,
        linkPosition: 'thread poll',
      });
    } catch {
      // setError('Error voting on poll');
    }
  };

  const updateCommentText = (event: ChangeEvent<HTMLTextAreaElement>) => {
    threadDetailDispatch({
      type: 'UPDATE_COMMENT_TEXT',
      payload: { comment: event.target.value as string },
    });
  };

  const dismissError = () => {
    threadDetailDispatch({
      type: 'DISMISS_ERROR',
    });
  };

  const handleCancelState = () => {
    threadDetailDispatch({
      type: 'RESET_COMMENT',
    });
  };

  const {
    author: threadAuthor,
    creationDate,
    description,
    exactSocialData,
    attachments,
    liked,
    poll,
    category,
  } = threadDetails;

  return (
    <Stack space="medium">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="spaceBetween"
        paddingTop="small"
      >
        <ThreadPersonalDetails
          author={threadAuthor}
          postingDate={creationDate}
        />
        <OptionsButton />
      </Box>
      <Stack space="xsmall">
        <ThreadContent
          text={description}
          images={attachments ?? []}
          poll={poll}
          voted={isVoted}
          onVote={handlePollClick}
        />
        <Box paddingTop="xsmall">
          <a
            key={`${category.name}-${category.categoryId}`}
            href={category.url}
            style={{ textDecoration: 'none' }}
            rel="nofollow"
          >
            <Text size="small" tone="formAccent" weight="medium">
              {category.name}
            </Text>
          </a>
        </Box>
        <ReactionBar
          likeLabel={exactSocialData?.likeCountLabel}
          likeStatus={liked}
          onLikeClick={handleLikeClick}
          commentLabel={exactSocialData?.commentAndReplyCountLabel}
          onCommentClick={handleCommentClick}
        />
        <CommentCTA onClick={handleCommentClick} />
      </Stack>
      {showCommentTextArea && (
        <Box
          padding="medium"
          className={styles.ContainerCommentTextArea}
          display={{ mobile: 'none', tablet: 'block', desktop: 'block' }}
        >
          <CommentTextArea
            id="commentTextArea"
            ariaLabel="Comment Text Area"
            value={commentText}
            author={author}
            onChange={updateCommentText}
            handleSubmit={handleCommentSubmit}
            error={error}
            isOpen={showCommentTextArea}
            onClose={handleCancelState}
            handleDismissError={dismissError}
            discardState={discardState}
            handleDiscard={handleDiscard}
            handleDiscardCancel={handleDiscardCancel}
          />
        </Box>
      )}
    </Stack>
  );
};
